import React, { useEffect, useRef, useState } from 'react';
import DateCalendarPopover from 'components/forms/elements/DateCalendarPopover';
import { theme } from 'styles/theme';
import {
  toDisplayDate,
  toStorageDate,
  todayStorage,
  isDisabledDate,
} from 'utils/datePickerUtils';

const wrapStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minHeight: 0,
  boxSizing: 'border-box',
};

const inputStyle = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  height: '100%',
  minHeight: 0,
  padding: '2px 22px 2px 4px',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: 'inherit',
  boxSizing: 'border-box',
};

const calBtnStyle = {
  position: 'absolute',
  right: 2,
  top: '50%',
  transform: 'translateY(-50%)',
  width: '18px',
  height: '18px',
  margin: 0,
  padding: 0,
  border: 'none',
  borderRadius: 2,
  background: 'transparent',
  color: theme.colors.text.secondary,
  fontSize: 12,
  lineHeight: 1,
  cursor: 'pointer',
};

/**
 * TanStack 그리드 셀 - 날짜 인라인 에디터 (DateCalendarPopover)
 * @param {string|Date} value - 표시/초기값
 * @param {Function} onSave - (value: string) => void (yyyy-MM-dd)
 * @param {Object} meta - 컬럼 meta (format, min, max, showPresets, presetSet 등)
 */
const DateCellEditor = ({
  value,
  onSave,
  onCancel,
  onBoundaryArrow,
  onEnterMoveDown,
  onEnterMoveUp,
  onTabMove,
  onFillSelection,
  meta = {},
}) => {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const openRef = useRef(false);
  const aliveRef = useRef(true);
  /** 오버레이 닫을 때 blur→저장·편집종료가 연쇄되지 않도록 한 틱 무시 */
  const ignoreBlurRef = useRef(false);
  const graceUntilRef = useRef(0);
  const textRef = useRef(toDisplayDate(value));
  const [text, setText] = useState(toDisplayDate(value));
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    aliveRef.current = true;
    graceUntilRef.current = Date.now() + 250;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    const display = toDisplayDate(value);
    setText(display);
    textRef.current = display;
  }, [value]);

  const setTextBoth = (next) => {
    textRef.current = next;
    setText(next);
  };

  const closePicker = () => {
    openRef.current = false;
    setOpen(false);
  };

  /** 캘린더만 닫고 셀 편집은 유지 (오버레이·Esc) */
  const dismissPickerKeepEdit = () => {
    closePicker();
    ignoreBlurRef.current = true;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      ignoreBlurRef.current = false;
    });
  };

  const commitValue = (raw, opts = {}) => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      if (!meta.required) {
        if (opts.closeEdit !== false) closePicker();
        onSave?.('');
      } else {
        setTextBoth(toDisplayDate(value));
      }
      return true;
    }
    const stor = toStorageDate(trimmed);
    if (!stor || isDisabledDate(stor, meta.min, meta.max)) {
      setTextBoth(toDisplayDate(value));
      return false;
    }
    const display = toDisplayDate(stor);
    setTextBoth(display);
    if (opts.closeEdit !== false) closePicker();
    onSave?.(display);
    return true;
  };

  const openPicker = () => {
    const rect = wrapRef.current?.getBoundingClientRect?.();
    if (rect) {
      setAnchor({ top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right });
    }
    openRef.current = true;
    setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const ok = commitValue(text);
      if (!ok) return;
      if (e.ctrlKey || e.metaKey) {
        onFillSelection?.(text);
        return;
      }
      if (e.shiftKey) onEnterMoveUp?.();
      else onEnterMoveDown?.();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      commitValue(text, { closeEdit: false });
      onTabMove?.(e.shiftKey ? 'left' : 'right');
      return;
    }
    if (e.key === 'Escape') {
      if (openRef.current) {
        dismissPickerKeepEdit();
        return;
      }
      onCancel?.();
      return;
    }
    if (e.key === 'ArrowDown' && (e.altKey || e.ctrlKey)) {
      e.preventDefault();
      openPicker();
      return;
    }
    const input = e.target;
    const v = input.value ?? '';
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    if (e.key === 'ArrowLeft' && start === 0 && end === 0) {
      e.preventDefault();
      commitValue(v, { closeEdit: false });
      onBoundaryArrow?.('left');
      return;
    }
    if (e.key === 'ArrowRight' && start === v.length && end === v.length) {
      e.preventDefault();
      commitValue(v, { closeEdit: false });
      onBoundaryArrow?.('right');
    }
  };

  const handleBlur = () => {
    // 캘린더(포털) 클릭 시 blur가 먼저 오면 편집 종료·언마운트로 선택이 무효화됨 → 열린 동안은 무시
    if (openRef.current || ignoreBlurRef.current) return;
    window.setTimeout(() => {
      if (!aliveRef.current) return;
      if (openRef.current || ignoreBlurRef.current) return;
      const ae = document.activeElement;
      if (ae === inputRef.current || wrapRef.current?.contains(ae)) return;
      if (Date.now() < graceUntilRef.current) {
        inputRef.current?.focus();
        return;
      }
      commitValue(textRef.current);
    }, 0);
  };

  const showPresets = meta.showPresets === true;
  const storValue = toStorageDate(value) || toStorageDate(text) || todayStorage();

  return (
    <div ref={wrapRef} style={wrapStyle}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="YYYY-MM-DD"
        title="직접 입력 또는 캘린더에서 선택 (▤ 또는 Alt+↓)"
        value={text}
        style={inputStyle}
        onChange={(e) => setTextBoth(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
      />
      <button
        type="button"
        tabIndex={-1}
        title="캘린더 열기"
        aria-label="캘린더 열기"
        style={calBtnStyle}
        onMouseDown={(e) => e.preventDefault()}
        onClick={openPicker}
      >
        ▤
      </button>
      {open && (
        <DateCalendarPopover
          mode="single"
          value={storValue}
          min={meta.min}
          max={meta.max}
          showTodayShortcut
          showPresets={showPresets}
          presetSet={meta.presetSet || 'minimal'}
          fiscalYear={meta.fiscalYear}
          anchorFrom={meta.anchorFrom}
          yearPreset={meta.yearPreset || 'calendar'}
          anchorRect={anchor}
          onClose={dismissPickerKeepEdit}
          onSelect={(stor) => {
            if (isDisabledDate(stor, meta.min, meta.max)) return;
            commitValue(toDisplayDate(stor));
          }}
        />
      )}
    </div>
  );
};

export default DateCellEditor;
